import { cn } from "@repo/shared/utils";
import type { User } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/Base/Popover";
import { UserRoundIcon } from "lucide-react";
import { useState } from "react";

export interface PersonListProps {
  users: User[];
  currentUser?: string;
  innerLabel?: string;
  includeNA?: boolean | string;
  includeNoOne?: boolean | string;
  inactiveButtonStyles?: string;
  activeButtonStyles?: string;
  onPickUser?: PersonSelectorProps["onPickUser"];
  disableSpecific?: string[];
}

export const PersonList = ({
  users,
  currentUser,
  innerLabel,
  includeNA,
  includeNoOne,
  inactiveButtonStyles,
  activeButtonStyles,
  onPickUser,
  disableSpecific = [],
}: PersonListProps) => {
  return (
    <div className="w-96 max-h-80 flex">
      <div className="flex flex-row gap-2 flex-wrap items-stretch flex-grow">
        {!!innerLabel && <h2 className="text-lg w-full">{innerLabel}</h2>}
        {!!includeNA && (
          <Button
            key="null"
            disabled={disableSpecific.includes("null")}
            className={
              currentUser === "null" ? activeButtonStyles : inactiveButtonStyles
            }
            onClick={() => onPickUser?.("null")}
          >
            {typeof includeNA === "string" ? includeNA : "N/A"}
          </Button>
        )}
        {!!includeNoOne && (
          <Button
            key="none"
            disabled={disableSpecific.includes("none")}
            className={
              currentUser === "none" ? activeButtonStyles : inactiveButtonStyles
            }
            onClick={() => onPickUser?.("none")}
          >
            {typeof includeNoOne === "string" ? includeNoOne : "Nobody"}
          </Button>
        )}
        {users.map((user) => (
          <Button
            key={user.id}
            disabled={disableSpecific.includes(user.id)}
            className={
              currentUser === user.id
                ? activeButtonStyles
                : inactiveButtonStyles
            }
            onClick={() => onPickUser?.(user.id)}
          >
            {user.firstName}
            <br />
            {user.lastName}
          </Button>
        ))}
      </div>
    </div>
  );
};

export interface PersonSelectorProps {
  users: User[];
  currentUser?: string;
  innerLabel?: string;
  includeNoOne?: boolean;
  includeNA?: boolean;
  placeholder?: string;
  className?: string;
  buttonStyles?: string;
  inactiveButtonStyles?: string;
  activeButtonStyles?: string;
  onPickUser?: (userId: string) => void;
  openInitially?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  disabled?: boolean;
  disableSpecific?: string[];
}

export const PersonSelector = ({
  users,
  currentUser,
  innerLabel,
  includeNoOne = false,
  includeNA = false,
  placeholder = "Select User",
  className,
  buttonStyles,
  inactiveButtonStyles,
  activeButtonStyles,
  onPickUser,
  openInitially = false,
  open: outerOpen,
  setOpen: outerSetOpen,
  disabled = false,
  disableSpecific = [],
}: PersonSelectorProps) => {
  const [innerOpen, innerSetOpen] = useState(openInitially);
  const open = outerOpen ?? innerOpen;
  const setOpen = outerSetOpen ?? innerSetOpen;

  const userObj = users.find((user) => user.id === currentUser);

  const baseButtonStyles = /*@tw*/ "h-auto border-[2px] border-primary";
  inactiveButtonStyles = cn(
    baseButtonStyles,
    /*@tw*/ "bg-transparent hover:bg-transparent text-primary",
    buttonStyles,
    inactiveButtonStyles
  );
  activeButtonStyles = cn(
    baseButtonStyles,
    /*@tw*/ "bg-primary hover:bg-primary/90",
    buttonStyles,
    activeButtonStyles
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          className={cn("min-w-8 h-8 p-0 pr-2", className)}
        >
          <UserRoundIcon className="mx-1" />
          {userObj ? (
            <>
              {userObj?.firstName} {userObj?.lastName}
            </>
          ) : currentUser === "null" ? (
            <em>{typeof includeNA === "string" ? includeNA : "N/A"}</em>
          ) : currentUser === "none" ? (
            <em>
              {typeof includeNoOne === "string" ? includeNoOne : "Nobody"}
            </em>
          ) : (
            <em>{placeholder}</em>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full h-full p-2 rounded-xl"
        side="bottom"
        align="start"
      >
        <PersonList
          users={users}
          currentUser={currentUser}
          innerLabel={innerLabel}
          includeNA={includeNA}
          includeNoOne={includeNoOne}
          inactiveButtonStyles={inactiveButtonStyles}
          activeButtonStyles={activeButtonStyles}
          disableSpecific={disableSpecific}
          onPickUser={(userId) => {
            setOpen(false);
            onPickUser?.(userId);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
